import {
  ContentListAccordian,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import {
  QuestionModuleMap,
  TaskMetaInfo,
  ChapterInfo,
} from '@protos/learning_management/lms.db_pb';

const styles: IStyles = {
  root: {
    width: { md: '100%' },
    border: '1px solid #E0E2E4',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
    padding: { xs: pxToRem(10), md: pxTovW(15) },
    marginY: { xs: pxToRem(10), md: pxTovW(10) },
    background: '#FFFFFF',
    boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: { xs: pxToRem(15), md: pxTovW(20) },
  },
  editButton: {
    p: '0',
    minWidth: 'unset',
    borderRadius: 0,
    overflow: 'visible',
    transition: 'transform 0.15s ease, filter 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: 'transparent',
      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35))',
    },
  },
  chapterRow: {
    width: { xs: '100%', md: pxTovW(500), lg: pxTovW(400) },
    display: { md: 'flex' },
    alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },
  chapterHeaderContent: {
    display: 'flex',
    width: '100%',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(5) },
  },
  correctIcon: {
    height: { xs: pxToRem(20), md: pxTovW(25), lg: pxTovW(20) },
    flexShrink: 0,
  },
  chapterHeaderTextCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(5), md: pxTovW(8) },
    flexGrow: 1,
    minWidth: 0,
  },
  chapterTitleRow: {
    display: 'flex',
    gap: pxToRem(5),
    alignItems: 'center',
    width: '100%',
  },
  chapterTitle: {
    fontWeight: 'bold',
    overflow: 'hidden',
    marginY: 'auto',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  countText: {
    fontWeight: 'bold',
  },
  topicRow: {
    width: '100%',
    height: { xs: '30px', md: '25px' },
    display: 'flex',
    gap: pxTovW(10),
    alignItems: 'center',
    paddingY: pxTovW(10),
  },
  topicNameRow: {
    display: 'flex',
    gap: pxToRem(5),
    alignItems: 'center',
    width: '100%',
  },
  topicName: {
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

interface IProps {
  assessmentTaskInfo?: TaskMetaInfo;
  editClickHandler?: () => void;
  questionModuleMap?: QuestionModuleMap[];
  currentSectionChapterInfo?: ChapterInfo;
  hideQuestionCounts?: boolean;
}

export const SelectedChaptersV2 = (props: IProps) => {
  const {
    assessmentTaskInfo,
    editClickHandler,
    questionModuleMap,
    currentSectionChapterInfo,
    hideQuestionCounts = false,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!assessmentTaskInfo || !assessmentTaskInfo.chapterInfo) {
    return null;
  }

  const chapterCount: Record<number, number> = {};
  const topicCount: Record<number, number> = {};
  if (!hideQuestionCounts) {
    assessmentTaskInfo.chapterInfo.chapterTopicsInfo?.forEach((chapter) => {
      const chapterId = chapter.chapterId;
      const topics = chapter.topicInfo.map((t) => t.topicId);
      topics.forEach((topicId) => {
        const questionCount = questionModuleMap?.filter(
          (q) => q.topicId === topicId
        ).length;
        topicCount[topicId] = (topicCount[topicId] || 0) + (questionCount || 0);
        chapterCount[chapterId] =
          (chapterCount[chapterId] || 0) + (questionCount || 0);
      });
      const questionCount = questionModuleMap?.filter(
        (q) =>
          q.chapterId === chapterId &&
          ((q.topicId && !topics.includes(q.topicId)) || !q.topicId)
      ).length;
      chapterCount[chapterId] =
        (chapterCount[chapterId] || 0) + (questionCount || 0);
    });
  }

  return (
    <Box sx={styles.root}>
      {assessmentTaskInfo?.chapterInfo?.chapterTopicsInfo?.length > 0 ? (
        <>
          <Box sx={styles.headerRow}>
            <Typography variant={isMobile ? 'h2' : 'h3'} fontWeight="bold">
              What's Covered
            </Typography>

            {editClickHandler && (
              <Button sx={styles.editButton} onClick={editClickHandler}>
                <IconWrapper
                  name="edit"
                  type="png"
                  parentFolder="icons"
                  size="large"
                />
              </Button>
            )}
          </Box>

          {currentSectionChapterInfo?.chapterTopicsInfo?.map(
            (chapter, index) => (
              <Box key={index} sx={styles.chapterRow}>
                <ContentListAccordian
                  key={index}
                  variant="medium"
                  HeaderContent={
                    <Box sx={styles.chapterHeaderContent}>
                      <IconWrapper
                        name="correct"
                        size="md"
                        type="png"
                        parentFolder="icons"
                        customSx={styles.correctIcon}
                      />
                      <Box sx={styles.chapterHeaderTextCol}>
                        <Box sx={styles.chapterTitleRow}>
                          <Typography variant="h4" sx={styles.chapterTitle}>
                            {`${
                              chapter.chapterNo ? `${chapter.chapterNo}.` : ''
                            } ${chapter.name}`}{' '}
                          </Typography>
                          {!hideQuestionCounts &&
                          chapterCount[chapter.chapterId] > 0 ? (
                            <Typography variant="h4" sx={styles.countText}>
                              [{chapterCount[chapter.chapterId]}]
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography color="primary" variant="h5">
                          {chapter.topicInfo.length} Topics
                        </Typography>
                      </Box>
                    </Box>
                  }
                  expandDisabled={false}
                  listRightPadding="10px"
                  ListItemArray={chapter.topicInfo.map((topic) => (
                    <Box sx={styles.topicRow}>
                      <Box sx={styles.topicNameRow}>
                        <Typography variant="h4" sx={styles.topicName}>
                          {topic.name}
                        </Typography>
                        {!hideQuestionCounts &&
                        topicCount[topic.topicId] > 0 ? (
                          <Typography variant="h4" sx={styles.countText}>
                            [{topicCount[topic.topicId]}]
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  ))}
                />
              </Box>
            )
          )}
        </>
      ) : (
        <></>
      )}
    </Box>
  );
};
